import { useEffect, useRef } from "react";
import { useToast } from "../components/Toast";

interface UseErrorToastOptions {
  /**
   * Whether an error state is active
   */
  isError: boolean;

  /**
   * Whether data is currently being fetched
   */
  isFetching: boolean;

  /**
   * Whether data is in initial loading state
   */
  isLoading: boolean;

  /**
   * Error message to display in toast
   */
  errorMessage: string;

  /**
   * Optional error key for tracking (useful when switching between tabs/views)
   * If provided, errors will be tracked separately for each key
   */
  errorKey?: string | number;

  /**
   * Optional delay before component is ready to show errors (default: 500ms)
   * Prevents showing stale cached errors during skeleton loading on navigation
   */
  mountDelay?: number;

  /**
   * Optional dependencies to reset error tracking
   * Example: [activeTab, currentView]
   */
  resetDependencies?: any[];
}

/**
 * Custom hook for handling error toast notifications with:
 * - Deduplication (shows error only once)
 * - Mount delay (prevents stale errors during skeleton loading)
 * - Auto-reset on error cleared
 * - Optional reset on dependency changes (tab/view switching)
 * - Cleanup on unmount
 *
 * @example
 * ```tsx
 * // Simple usage
 * useErrorToast({
 *   isError,
 *   isFetching,
 *   isLoading,
 *   errorMessage: "Failed to load data. Please try again."
 * });
 *
 * // With tab tracking
 * useErrorToast({
 *   isError: currentIsError,
 *   isFetching,
 *   isLoading,
 *   errorMessage: `Failed to load ${viewName} data.`,
 *   errorKey: `${viewName}-${activeTab}`,
 *   resetDependencies: [activeTab]
 * });
 * ```
 */
export const useErrorToast = ({
  isError,
  isFetching,
  isLoading,
  errorMessage,
  errorKey,
  mountDelay = 500,
  resetDependencies = [],
}: UseErrorToastOptions): void => {
  const toast = useToast();
  const shownErrorRef = useRef<string | number | boolean | null>(null);
  const mountedRef = useRef<boolean>(false);

  // Reset error tracking AND mountedRef when dependencies change (e.g., tab/view switching)
  useEffect(() => {
    if (resetDependencies.length > 0) {
      shownErrorRef.current = null;
      mountedRef.current = false; // Reset mounted state on tab/view change
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetDependencies);

  // Show toast notification when error occurs (only once per error)
  useEffect(() => {
    // Only show error toast after component has been mounted for a bit
    // This prevents showing stale errors during skeleton loading on navigation
    if (isError && !isFetching && !isLoading && mountedRef.current) {
      const currentErrorKey = errorKey !== undefined ? errorKey : true;

      // Only show toast if we haven't shown it for this error yet
      if (shownErrorRef.current !== currentErrorKey) {
        toast.error(errorMessage);
        shownErrorRef.current = currentErrorKey;
      }
    } else if (!isError) {
      // Reset when error is cleared
      shownErrorRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isFetching, isLoading, errorMessage, errorKey]);

  // Mark component as mounted after a short delay
  // Also re-run when resetDependencies change to restart the delay on tab/view switching
  useEffect(() => {
    mountedRef.current = false; // Reset immediately when dependencies change

    const timer = setTimeout(() => {
      mountedRef.current = true;
    }, mountDelay);

    return () => {
      clearTimeout(timer);
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mountDelay, ...resetDependencies]);

  // Cleanup: Reset error tracking on unmount
  useEffect(() => {
    return () => {
      shownErrorRef.current = null;
      mountedRef.current = false;
    };
  }, []);
};
